import { buttonClasses, type ButtonProps } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { COLORS_SCHEMA } from '../../palette';

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonOwnProps {
    active?: boolean;
    square?: boolean;
  }

  interface ButtonPropsColorOverrides {
    default: true;
  }

  interface ButtonPropsVariantOverrides {
    soft: true;
  }
  interface ButtonPropsSizeOverrides {
    xxsmall: true;
    xsmall: true;
    xlarge: true;
  }
}

export default function Button(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';
    if (!ownerState.color) ownerState.color = 'default';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const xSmallSize = ownerState.size === 'xsmall';

    const xxSmallSize = ownerState.size === 'xxsmall';

    const smallSize = ownerState.size === 'small';

    const mediumSize = ownerState.size === 'medium';

    const largeSize = ownerState.size === 'large';

    const xLargeSize = ownerState.size === 'xlarge';

    const isActive = Boolean(ownerState.active);

    const isSquare = Boolean(ownerState.square);

    const defaultStyle = {
      borderRadius: isSquare ? 0 : theme.spacing(0.5),
      gap: 4,
      minWidth: 'auto',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      textTransform: 'capitalize',
      boxSizing: 'border-box',
      lineHeight: 1,
      [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
        margin: '0!important',
        marginRight: '0!important',
        marginLeft: '0!important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          color: !isLight
            ? theme.palette.common.white
            : theme.palette.grey[800],
          backgroundColor: !isLight
            ? theme.palette.grey[800]
            : theme.palette.common.white,
          '&:hover': {
            backgroundColor: !isLight
              ? theme.palette.grey[700]
              : theme.palette.grey[400],
          },
        }),
        // OUTLINED
        ...(outlinedVariant && {
          borderColor: alpha(theme.palette.grey[500], 0.32),
          backgroundColor: theme.palette.common.white,
          // '&:hover': {
          //   backgroundColor: theme.palette.action.hover,
          // },
        }),
        // TEXT
        ...(textVariant &&
          {
            // color: theme.palette.text.primary,
            // backgroundColor: alpha(theme.palette.grey[500], 0.08),
            // '&:hover': {
            //   backgroundColor: theme.palette.action.hover,
            // },
          }),
      }),
      ...(outlinedVariant && {
        '&:hover': {
          borderColor: 'currentColor',
          // boxShadow: '0 0 0 0.5px currentColor',
        },
      }),
    };

    const colorStyle = Object.values(COLORS_SCHEMA).map((color) => ({
      ...(ownerState.color === color && {
        // CONTAINED
        ...(containedVariant && {
          backgroundColor: theme.palette[color][500],
          color: theme.palette[color].contrastText,
          border: `1px solid ${theme.palette[color][500]}`,
          '&:hover': {
            backgroundColor: theme.palette[color][600],
            border: `1px solid ${theme.palette[color][600]}`,
          },
          '&:focus': {
            border: `1px solid ${theme.palette[color][400]}`,
            backgroundColor: theme.palette[color][400],
            color:
              color === 'default'
                ? theme.palette.grey[600]
                : theme.palette[color].contrastText,
          },
        }),
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette[color][100],
          color: theme.palette[color][600],
          border: `none`,
          '&:hover': {
            backgroundColor: theme.palette[color][200],
          },
          '&:focus': {
            backgroundColor: theme.palette[color][300],
          },
          [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            color: theme.palette[color][600],
          },
        }),
        ...(outlinedVariant && {
          backgroundColor: theme.palette.common.white,
          color:
            color === 'default'
              ? theme.palette.grey[700]
              : theme.palette[color][500],
          border: `1px solid ${
            color === 'default'
              ? theme.palette.grey[400]
              : theme.palette[color][500]
          }`,
          [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            color: theme.palette[color][500],
          },
          '&:hover': {
            backgroundColor: theme.palette.common.white,
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color][600],
            border: `1px solid ${
              color === 'default'
                ? theme.palette.grey[400]
                : theme.palette[color][600]
            }`,
            [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][600],
            },
          },
          '&:focus': {
            backgroundColor: theme.palette.common.white,
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color][400],
            border: `1px solid ${
              color === 'default'
                ? theme.palette.grey[500]
                : theme.palette[color][400]
            }`,
            [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][400],
            },
          },
        }),
        ...(textVariant && {
          backgroundColor: 'transparent',
          color:
            color === 'default'
              ? theme.palette.grey[700]
              : theme.palette[color][500],
          border: `none`,
          [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color][500],
          },
          '&:hover': {
            backgroundColor: theme.palette.common.white,
          },
          '&:focus': {
            color:
              color === 'default'
                ? theme.palette.grey[700]
                : theme.palette[color][400],
            [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
              color:
                color === 'default'
                  ? theme.palette.grey[700]
                  : theme.palette[color][400],
            },
          },
        }),
      }),
    }));

    const disabledState = {
      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[400],
        ...(inheritColor && {
          [`& > .${buttonClasses.startIcon} > svg`]: {
            color: theme.palette.grey[400],
          },
        }),
        ...(containedVariant && {
          borderColor: theme.palette.grey[100],
          [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            margin: 0,
            color: theme.palette.grey[400],
          },
        }),
        // OUTLINED
        ...(outlinedVariant && {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.grey[400],
          [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            margin: 0,
            color: theme.palette.grey[400],
          },
        }),
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[400],
          [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            margin: 0,
            color: theme.palette.grey[400],
          },
        }),
        ...(textVariant && {
          backgroundColor: 'transparent',
          color: theme.palette.grey[400],
          border: 'none',
          [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
            margin: 0,
            color: theme.palette.grey[400],
          },
        }),
      },
    };

    const size = {
      ...(xxSmallSize && {
        height: 16,
        fontSize: theme.typography.pxToRem(10),
        padding: theme.spacing(0.5),
        [`& > .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 16,
          width: 16,
          ' > svg': {
            height: 16,
            width: 16,
          },
        },
      }),
      ...(xSmallSize && {
        height: 24,
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(0.5),
        borderRadius: isSquare ? 0 : theme.spacing(0.5),
        [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 12,
          width: 12,
          ' > svg': {
            height: 12,
            width: 12,
          },
        },
      }),
      ...(smallSize && {
        height: 32,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightSemiBold,
        borderRadius: isSquare ? 0 : theme.spacing(0.5),
        padding: theme.spacing(1),
        [`.${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 16,
          width: 16,
          ' > svg': {
            height: 16,
            width: 16,
          },
        },
      }),
      ...(mediumSize && {
        height: 40,
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightSemiBold,
        gap: 8,
        padding: theme.spacing(1, 2),
        [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 20,
          width: 20,
          ' > svg': {
            height: 20,
            width: 20,
          },
        },
      }),
      ...(largeSize && {
        height: 48,
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightSemiBold,
        gap: 8,
        padding: theme.spacing(1, 2),
        [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 24,
          width: 24,
          ' > svg': {
            height: 24,
            width: 24,
          },
        },
      }),
      ...(xLargeSize && {
        height: 56,
        fontSize: theme.typography.pxToRem(20),
        fontWeight: theme.typography.fontWeightSemiBold,
        padding: theme.spacing(1.5, 2),
        gap: 8,
        [`> .${buttonClasses.startIcon},.${buttonClasses.endIcon}`]: {
          height: 24,
          width: 24,
          ' > svg': {
            height: 24,
            width: 24,
          },
        },
      }),
    };

    return {
      ...defaultStyle,
      ...disabledState,
      ...size,
      ...colorStyle.reduce((acc, style) => ({ ...acc, ...style }), {}),
    };
  };

  return {
    MuiButton: {
      defaultProps: {
        color: 'default',
        disableElevation: true,
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
